// components/TrimListDataSheet.jsx
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Tooltip,
  Divider,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
} from "@mui/icons-material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

// ── helpers ───────────────────────────────────────────────────────────────────

function safeEntries(obj) {
  return Object.entries(obj ?? {});
}

function displayValue(value) {
  return value !== undefined && value !== null && value !== "" ? value : "–";
}

// ── pure sub-components ───────────────────────────────────────────────────────

function SectionHeader({ title }) {
  return (
    <Box
      sx={{
        bgcolor: "primary.main",
        // color: "#fff",
        px: 1.5,
        py: 0.6,
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        borderLeft: "4px solid",
        borderColor: "primary.dark",
      }}
    >
      - {title}
    </Box>
  );
}

function TableHeader() {
  const cols = ["Field", "Value", "Yes / No", "Status", "Remarks"];
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "170px 1fr 110px 70px 1fr",
        // bgcolor: "grey.50",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      {cols.map((h, i) => (
        <Typography
          key={h}
          variant="caption"
          sx={{
            px: 1,
            py: 0.5,
            fontWeight: 700,
            color: i === 0 ? "text.secondary" : "primary.main",
            borderRight: i < cols.length - 1 ? "1px solid" : "none",
            borderColor: "divider",
            textAlign: i === 0 ? "left" : "center",
          }}
        >
          {h}
        </Typography>
      ))}
    </Box>
  );
}

function VendorCells({ section, label, fieldState, isEditable, onChange }) {
  const { radio, remarks } = fieldState ?? { radio: null, remarks: "" };

  const status =
    radio === "yes"
      ? { color: "success.main", icon: "✓" }
      : radio === "no"
        ? { color: "error.main", icon: "✕" }
        : { color: "text.disabled", icon: "—" };

  return (
    <>
      {/* Yes / No */}
      <Box
        sx={{
          px: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRight: "1px solid",
          borderColor: "divider",
        }}
      >
        {isEditable ? (
          <RadioGroup
            row
            value={radio ?? ""}
            onChange={(e) => onChange(section, label, "radio", e.target.value)}
          >
            <FormControlLabel
              value="yes"
              control={<Radio size="small" sx={{ p: 0.3 }} />}
              label={<Typography variant="caption">Y</Typography>}
              // sx={{ mr: 0.5 }}
            />
            <FormControlLabel
              value="no"
              control={<Radio size="small" sx={{ p: 0.3 }} />}
              label={<Typography variant="caption">N</Typography>}
              sx={{ mr: 0 }}
            />
          </RadioGroup>
        ) : (
          <Typography
            variant="caption"
            sx={{ color: status.color, fontWeight: 600 }}
          >
            {radio ? radio.toUpperCase() : "—"}
          </Typography>
        )}
      </Box>

      {/* Status icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRight: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography sx={{ color: status.color, fontWeight: 600 }}>
          {status.icon}
        </Typography>
      </Box>

      {/* Remarks */}
      <Box sx={{ px: 1, display: "flex", alignItems: "center" }}>
        {isEditable ? (
          <TextField
            size="small"
            fullWidth
            placeholder="Remarks…"
            value={remarks ?? ""}
            onChange={(e) =>
              onChange(section, label, "remarks", e.target.value)
            }
            inputProps={{ style: { fontSize: 12, padding: "3px 8px" } }}
          />
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            fontStyle="italic"
          >
            {remarks || "—"}
          </Typography>
        )}
      </Box>
    </>
  );
}

function DataRow({
  section,
  label,
  value,
  isEditable,
  fieldState,
  onChange,
  isEven,
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "170px 1fr 110px 70px 1fr",
        // bgcolor: isEven ? "white" : "grey.50",
        borderBottom: "1px solid",
        borderColor: "divider",
        minHeight: 32,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          px: 1,
          py: 0.75,
          color: "text.secondary",
          borderRight: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          px: 1,
          py: 0.75,
          borderRight: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
        }}
      >
        {displayValue(value)}
      </Typography>
      {section !== "Client Information" && (
        <VendorCells
          section={section}
          label={label}
          fieldState={fieldState}
          isEditable={isEditable}
          onChange={onChange}
        />
      )}
    </Box>
  );
}

function EmptyState({ message }) {
  return (
    <Box sx={{ py: 6, textAlign: "center" }}>
      <Typography color="text.secondary" variant="body2">
        {message}
      </Typography>
    </Box>
  );
}

// ── main component ────────────────────────────────────────────────────────────

export default function TrimListDataSheet({
  data,
  isEditable,
  onEditToggle,
  onSave,
  getFieldState,
  onChange,
  onClose,
  vendor,
  isVendor,
}) {
  // Guard 1: no data prop
  if (!data) {
    return (
      <Paper elevation={3} sx={{ width: 820, borderRadius: 1, p: 4 }}>
        <EmptyState message="No data available." />
      </Paper>
    );
  }

  const dataSheet = data?.dataSheet ?? {};
  const tagNumber =
    dataSheet?.["Tag & Line Information"]?.["Tag Number"] ?? "—";
  const sections = safeEntries(dataSheet);

  return (
    <Paper
      elevation={3}
      sx={{ width: 820, borderRadius: 1, overflow: "hidden" }}
    >
      {/* ── Header ── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2.5,
          py: 1.8,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        }}
      >
        {/* Left: Title & Meta */}
        <Box>
          <Stack direction="row" alignItems="center" gap={1} mt={0.3}>
            <Typography variant="h6" fontWeight={700}>
              Data Sheet
            </Typography>

            <Chip
              size="small"
              label={`Tag: ${tagNumber}`}
              sx={{ height: 20 }}
            />
          </Stack>
        </Box>

        {/* Center: Metrics */}
        <Stack
          direction="row"
          gap={1.5}
          sx={{
            display: { xs: "none", md: "flex" },
          }}
        >
          {/* Lead Time */}
          <Box
            sx={{
              px: 1.5,
              py: 0.8,
              borderRadius: 2,
              bgcolor: "action.hover",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <AccessTimeIcon fontSize="small" color="primary" />

            <Box>
              <Typography variant="caption" color="text.secondary">
                Lead Time
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {data?.time || "—"}
              </Typography>
            </Box>
          </Box>

          {/* Cost */}
          <Box
            sx={{
              px: 1.5,
              py: 0.8,
              borderRadius: 2,
              bgcolor: "action.hover",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <AttachMoneyIcon fontSize="small" color="success" />

            <Box>
              <Typography variant="caption" color="text.secondary">
                Bid Cost
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {data?.cost || "—"}
              </Typography>
            </Box>
          </Box>
        </Stack>

        {/* Right: Vendor & Actions */}
        <Stack direction="row" alignItems="center" gap={1}>
          {/* Vendor Name */}
          <Box
            sx={{
              px: 1.5,
              py: 0.8,
              borderRadius: 2,
              bgcolor: "action.hover",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <AccountCircleIcon color="primary" />

            <Box>
              <Typography variant="caption" color="text.secondary">
                Vendor Name
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {vendor?.name || "Unknown Vendor"}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Stack>

      {/* ── Body ── */}
      <Box sx={{ maxHeight: 720, overflowY: "auto" }}>
        {/* Guard 2: empty dataSheet */}
        {sections.length === 0 ? (
          <EmptyState message="No sections found in data sheet." />
        ) : (
          sections.map(([section, fields]) => {
            const rows = safeEntries(fields);
            return (
              <Box key={section} sx={{ mb: "2px" }}>
                <SectionHeader title={section.toUpperCase()} />
                <TableHeader />
                {/* Guard 3: empty section */}
                {rows.length === 0 ? (
                  <EmptyState message={`No fields in "${section}".`} />
                ) : (
                  rows.map(([label, value], i) => (
                    <DataRow
                      key={label}
                      section={section}
                      label={label}
                      value={value}
                      isEditable={isEditable}
                      fieldState={getFieldState(section, label)}
                      onChange={onChange}
                      isEven={i % 2 === 0}
                    />
                  ))
                )}
              </Box>
            );
          })
        )}
      </Box>

      {/* ── Footer ── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1.5,
          py: 1,
          borderTop: "1px solid",
          borderColor: "divider",
          // bgcolor: "grey.50",
        }}
      >
        <Chip
          icon={<CheckCircleOutlineIcon fontSize="small" />}
          label="PnID data extraction completed"
          onDelete={() => {}}
          deleteIcon={<CloseIcon fontSize="small" />}
          size="small"
          sx={{
            // bgcolor: "#e8f5e9",
            border: "1px solid #a5d6a7",
            color: "success.dark",
            "& .MuiChip-icon": { color: "success.main" },
          }}
        />

        <Box>
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={onClose}
            sx={{ fontWeight: 700, mx: 1 }}
          >
            CLOSE
          </Button>

          {isVendor && (
            <Button
              size="small"
              variant={isEditable ? "contained" : "outlined"}
              startIcon={isEditable ? <SaveIcon /> : <EditIcon />}
              onClick={isEditable ? onSave : onEditToggle}
              sx={{
                textTransform: "none",
                px: 2,
              }}
            >
              {isEditable ? "Save" : "Edit"}
            </Button>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
